export type CoverageScopeFilter = 'required' | 'all'
export type CoverageCompletionFilter =
    | 'all'
    | 'missing'
    | 'complete'
    | 'required_missing'
export type CoveragePropertyStatusFilter =
    | 'all'
    | 'uploaded'
    | 'missing'
    | 'required_missing'

export interface CoveragePropertyLike {
    id: number
    name: string
    is_required: boolean
    has_document: boolean
}

export interface CoverageGroupLike<P extends CoveragePropertyLike> {
    typeId?: number
    typeName: string
    typeDescription?: string | null
    required_count: number
    properties: P[]
}

export interface CoverageFilterOptions {
    typeSearch: string
    scope: CoverageScopeFilter
    completion: CoverageCompletionFilter
    propertyStatus: CoveragePropertyStatusFilter
    selectedPropertyIds: number[] | null
}

export const applyCoverageFilters = <
    P extends CoveragePropertyLike,
    G extends CoverageGroupLike<P>
>(
    groups: G[],
    options: CoverageFilterOptions,
): Array<
    G & {
        properties: P[]
        required_count: number
        required_missing_count: number
        uploaded_count: number
        missing_count: number
    }
> => {
    const selectedSet =
        options.selectedPropertyIds == null
            ? null
            : new Set(options.selectedPropertyIds)
    const search = options.typeSearch.trim().toLowerCase()

    return groups
        .filter((group) => {
            if (options.scope === 'required' && group.required_count <= 0) {
                return false
            }
            if (!search) return true
            return (
                group.typeName.toLowerCase().includes(search) ||
                (group.typeDescription || '').toLowerCase().includes(search)
            )
        })
        .map((group) => {
            let properties = group.properties
            if (options.scope === 'required') {
                properties = properties.filter(
                    (property) => property.is_required,
                )
            }
            if (selectedSet) {
                properties = properties.filter((property) =>
                    selectedSet.has(property.id),
                )
            }
            if (options.propertyStatus === 'uploaded') {
                properties = properties.filter(
                    (property) => property.has_document,
                )
            } else if (options.propertyStatus === 'missing') {
                properties = properties.filter(
                    (property) => !property.has_document,
                )
            } else if (options.propertyStatus === 'required_missing') {
                properties = properties.filter(
                    (property) =>
                        property.is_required && !property.has_document,
                )
            }

            const uploadedCount = properties.filter(
                (property) => property.has_document,
            ).length
            const requiredCount = properties.filter(
                (property) => property.is_required,
            ).length
            const requiredMissingCount = properties.filter(
                (property) => property.is_required && !property.has_document,
            ).length

            return {
                ...group,
                properties,
                required_count: requiredCount,
                required_missing_count: requiredMissingCount,
                uploaded_count: uploadedCount,
                missing_count: properties.length - uploadedCount,
            }
        })
        .filter((group) => {
            if (options.completion === 'missing') {
                return group.missing_count > 0
            }
            if (options.completion === 'complete') {
                return group.properties.length > 0 && group.missing_count === 0
            }
            if (options.completion === 'required_missing') {
                return group.required_missing_count > 0
            }
            return true
        })
}

export interface PivotedPropertyType {
    typeId?: number
    typeName: string
    typeDescription?: string | null
    is_required: boolean
    has_document: boolean
    documents: Array<{
        id: number
        name: string
        document: string
        created_date: string
        modified_date: string
    }>
}

export interface PivotedPropertyGroup {
    propertyId: number
    propertyName: string
    types: PivotedPropertyType[]
    requiredMissingCount: number
    onFileCount: number
    totalCount: number
}

export const pivotCoverageByProperty = <
    P extends CoveragePropertyLike & {
        documents: Array<{
            id: number
            name: string
            document: string
            created_date: string
            modified_date: string
        }>
    },
    G extends CoverageGroupLike<P> & { typeDescription?: string | null }
>(
    filteredGroups: G[],
): PivotedPropertyGroup[] => {
    const propertyMap = new Map<number, PivotedPropertyGroup>()

    for (const group of filteredGroups) {
        for (const property of group.properties) {
            let entry = propertyMap.get(property.id)
            if (!entry) {
                entry = {
                    propertyId: property.id,
                    propertyName: property.name,
                    types: [],
                    requiredMissingCount: 0,
                    onFileCount: 0,
                    totalCount: 0,
                }
                propertyMap.set(property.id, entry)
            }
            entry.types.push({
                typeId: group.typeId,
                typeName: group.typeName,
                typeDescription: group.typeDescription,
                is_required: property.is_required,
                has_document: property.has_document,
                documents: property.documents,
            })
            entry.totalCount += 1
            if (property.has_document) entry.onFileCount += 1
            if (property.is_required && !property.has_document)
                entry.requiredMissingCount += 1
        }
    }

    return Array.from(propertyMap.values())
        .map((entry) => {
            entry.types.sort(
                (a: PivotedPropertyType, b: PivotedPropertyType) => {
                    const rank = (type: PivotedPropertyType) => {
                        if (type.is_required && !type.has_document) return 0
                        if (!type.has_document) return 1
                        return 2
                    }
                    const difference = rank(a) - rank(b)
                    if (difference !== 0) return difference
                    return a.typeName.localeCompare(b.typeName)
                },
            )
            return entry
        })
        .sort((a, b) => {
            if (a.requiredMissingCount > 0 && b.requiredMissingCount === 0)
                return -1
            if (a.requiredMissingCount === 0 && b.requiredMissingCount > 0)
                return 1
            return a.propertyName.localeCompare(b.propertyName)
        })
}

export const hasActiveCoverageFilters = (options: {
    typeSearch: string
    scope: CoverageScopeFilter
    completion: CoverageCompletionFilter
    propertyStatus: CoveragePropertyStatusFilter
}): boolean =>
    Boolean(options.typeSearch.trim()) ||
    options.scope !== 'required' ||
    options.completion !== 'all' ||
    options.propertyStatus !== 'all'
